import { useMediaQuery, Theme, Skeleton } from '@mui/material';
import { renderProp, RenderPropType } from '@procureai/ui-mui';
import { path as ramdaPath } from 'ramda';
import { Key, ReactElement } from 'react';

import {
  ActionCell,
  Table,
  TableBodyRow,
  TableCellInner,
  TableHeaderRow,
  TableCells,
  TableHeaderCell,
  TableBodyCell,
} from './ResponsiveTable.styled';
import { ResponsiveTableColumnOptions, ResponsiveTableRenderActionsProps } from './ResponsiveTable.types';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ResponsiveTableProps<D extends {} = {}> {
  columns: ResponsiveTableColumnOptions<D>[];
  rows: D[] | null | undefined;
  editing?: boolean;
  loading?: boolean;
  loadingRows?: boolean;
  dense?: boolean;
  getRowKey?: (row: any, rowIndex: number) => Key | null | undefined;
  renderActions?: RenderPropType<ResponsiveTableRenderActionsProps>;
}

const getRowKeyDefault = (_row: any, rowIndex: number) => `${rowIndex}`;

const emptyRows: any[] = [];
const dummyRows: any[] = [{ id: '1' }, { id: '2' }, { id: '3' }];

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const ResponsiveTable: <D extends {} = {}>(props: ResponsiveTableProps<D>) => ReactElement<any> | null = ({
  rows: rowsProps,
  columns,
  loading,
  renderActions,
  getRowKey = getRowKeyDefault,
}) => {
  const hideLabel = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'));
  const rows = (loading ? dummyRows : rowsProps) || emptyRows;

  return (
    <Table>
      {hideLabel && (
        <TableHeaderRow>
          <TableCells>
            {columns.map((column) => {
              const { HeadCell } = column;
              return HeadCell ? (
                <TableHeaderCell key={column.id} sx={column.sx}>
                  <TableCellInner>
                    {renderProp(HeadCell, {
                      column,
                      value: column.path?.length ? column.path : [column.id],
                      rowIndex: -1,
                    })}
                  </TableCellInner>
                </TableHeaderCell>
              ) : null;
            })}
          </TableCells>
          {renderActions ? <ActionCell /> : null}
        </TableHeaderRow>
      )}
      {rows.map((row, rowIndex) => {
        const key = getRowKey(row, rowIndex);
        return (
          <TableBodyRow key={key}>
            <TableCells>
              {columns.map((column) => {
                const value = column.path
                  ? column.getCellValue
                    ? column.getCellValue(row, column.path)
                    : ramdaPath(column.path, row)
                  : null;
                const CellRenderer = column.Cell;

                let content;

                if (loading && column.path) {
                  content = (
                    <TableCellInner>
                      <Skeleton />
                    </TableCellInner>
                  );
                } else if (column.path) {
                  content = (
                    <TableCellInner>
                      {CellRenderer ? renderProp(CellRenderer, { column, row, value, rowIndex, hideLabel }) : value}
                    </TableCellInner>
                  );
                }
                return (
                  <TableBodyCell key={column.id} sx={column.sx}>
                    {content}
                  </TableBodyCell>
                );
              })}
            </TableCells>
            {renderActions ? <ActionCell>{renderProp(renderActions, { row, rowIndex })}</ActionCell> : null}
          </TableBodyRow>
        );
      })}
    </Table>
  );
};
